(() => {
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const initialized = new WeakSet();
  const init = (root = document) => {
    const posters = [...root.querySelectorAll('video[data-hx-poster]')].filter(video => !initialized.has(video));
    const loadPoster = video => {
      video.poster = video.dataset.hxPoster;
      initialized.add(video);
    };
    if ('IntersectionObserver' in window) {
      const posterObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          loadPoster(entry.target);
          posterObserver.unobserve(entry.target);
        });
        if (posters.every(video => initialized.has(video))) posterObserver.disconnect();
      }, { rootMargin: '240px' });
      posters.forEach(video => posterObserver.observe(video));
    } else posters.forEach(loadPoster);
    root.querySelectorAll('[data-hx-cell-loop]').forEach(container => {
      if (initialized.has(container)) return;
      initialized.add(container);
      const first = container.querySelector('video');
      if (!first) return;
      const second = first.cloneNode(true);
      container.append(second);
      const films = [first, second];
      films.forEach(film => {
        film.autoplay = false;
        film.loop = false;
        film.muted = true;
        film.defaultMuted = true;
        film.playsInline = true;
        film.controls = false;
      });
      let active = 0, visible = false, loaded = false, running = false, blending = false;
      let epoch = 0, frame = 0, animation;
      const canPlay = () => container.isConnected && visible && !document.hidden && !reduced.matches;
      const position = () => films.forEach((film, index) => {
        film.style.opacity = running && index === active ? '1' : '0';
        film.style.zIndex = index === active ? '2' : '1';
      });
      const stop = () => {
        epoch++;
        cancelAnimationFrame(frame);
        frame = 0;
        running = false;
        blending = false;
        animation?.cancel();
        films.forEach(film => film.pause());
        position();
      };
      const fade = async () => {
        if (blending || !canPlay()) return;
        blending = true;
        const token = epoch, outgoing = films[active], next = 1 - active, incoming = films[next];
        try {
          incoming.currentTime = 0;
          incoming.style.opacity = '0';
          incoming.style.zIndex = '3';
          await incoming.play();
          if (token !== epoch || !canPlay()) { incoming.pause(); return; }
          animation = incoming.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 650, easing: 'linear', fill: 'forwards' });
          await animation.finished;
          if (token !== epoch || !canPlay()) return;
          active = next;
          position();
          animation.cancel();
          outgoing.pause();
          outgoing.currentTime = 0;
          blending = false;
        } catch {
          if (token !== epoch) return;
          blending = false;
          incoming.pause();
          outgoing.currentTime = 0;
          position();
          outgoing.play().catch(stop);
        }
      };
      const tick = () => {
        if (!canPlay()) { stop(); return; }
        const film = films[active];
        if (!blending && Number.isFinite(film.duration) && film.duration - film.currentTime < .73 && films[1 - active].readyState >= 2) fade();
        frame = requestAnimationFrame(tick);
      };
      const sync = async () => {
        if (!canPlay()) { stop(); return; }
        if (running) return;
        running = true;
        const token = epoch;
        if (!loaded) {
          films.forEach(film => { film.src = container.dataset.videoSrc; film.preload = 'auto'; film.load(); });
          loaded = true;
        }
        try {
          await films[active].play();
          if (token !== epoch || !canPlay()) return;
          position();
          frame = requestAnimationFrame(tick);
        } catch { if (token === epoch) stop(); }
      };
      films.forEach(film => film.addEventListener('ended', () => {
        if (film === films[active] && !blending && canPlay()) { film.currentTime = 0; film.play().catch(stop); }
      }));
      const observer = new IntersectionObserver(entries => {
        visible = entries[0].isIntersecting;
        sync();
      }, { threshold: .05 });
      observer.observe(container);
      document.addEventListener('visibilitychange', sync);
      reduced.addEventListener('change', sync);
      document.addEventListener('shopify:section:unload', event => {
        if (!event.target.contains(container)) return;
        stop(); observer.disconnect();
        document.removeEventListener('visibilitychange', sync);
        reduced.removeEventListener('change', sync);
      });
    });
    root.querySelectorAll('[data-hx-proof]').forEach(section => {
      if (initialized.has(section)) return;
      initialized.add(section);
      const observer = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        observer.disconnect();
        if (reduced.matches) return;
        section.querySelectorAll('[data-hx-count]').forEach(number => {
          const target = Number(number.dataset.hxCount), decimals = Number(number.dataset.hxCountDecimals || 0);
          const formatter = new Intl.NumberFormat(document.documentElement.lang || 'fr', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
          const start = performance.now();
          const update = now => {
            const progress = Math.max(0, Math.min(1, (now - start) / 1500));
            const value = target * (1 - Math.pow(1 - progress, 4));
            number.textContent = (number.dataset.hxCountPrefix || '') + formatter.format(value) + (number.dataset.hxCountSuffix || '');
            if (progress < 1 && number.isConnected) requestAnimationFrame(update);
          };
          requestAnimationFrame(update);
        });
      }, { threshold: .25 });
      observer.observe(section.querySelector('.hx-brand-stats') || section);
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
})();

